import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Search, Filter, Download, FileSpreadsheet, FileText, Trash2,
    ToggleLeft, ToggleRight, Users, UserCheck, Clock, TrendingUp, MoreHorizontal,
    Eye, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import DataTable from "@/components/common/DataTable";
import UserCell from "@/components/common/UserCell";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import Can, { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import { exportToExcel, exportToPdf } from "@/lib/exports";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

function StatMini({ Icon, label, value, accent }) {
    return (
        <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-[11.5px] text-muted-foreground">{label}</div>
                    <div className="text-[20px] font-bold leading-tight tracking-tight">{value}</div>
                </div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function PersonnelList() {
    const canView = useCan("employees.view");
    const canCreate = useCan("employees.create");
    const canEdit = useCan("employees.edit");
    const canDelete = useCan("employees.delete");
    const canManage = useCan("employees.manage");
    const canExport = useCan("employees.export");
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [department, setDepartment] = useState("");
    const [role, setRole] = useState("");
    const [status, setStatus] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState({ open: false, action: null });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize, sort, order };
            if (debouncedSearch) params.search = debouncedSearch;
            if (department) params.department = department;
            if (role) params.role = role;
            if (status) params.status = status;
            const { data } = await api.get("/employees", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Personel listesi alınamadı.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, sort, order, debouncedSearch, department, role, status]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    useEffect(() => {
        (async () => {
            try {
                const [d, r] = await Promise.all([
                    api.get("/departments"),
                    api.get("/roles"),
                ]);
                setDepartments(d.data.items || []);
                setRoles(r.data.items || []);
            } catch (_) { /* ignore */ }
        })();
    }, []);

    const stats = useMemo(() => {
        const active = rows.filter((r) => r.status === "active").length;
        const onLeave = rows.filter((r) => r.status === "on_leave").length;
        return { total, active, onLeave };
    }, [rows, total]);

    const doBulk = async () => {
        if (!bulkConfirm.action || selectedIds.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/employees/bulk", { ids: selectedIds, action: bulkConfirm.action });
            toast.success(`${data.affected} personel etkilendi.`);
            setSelectedIds([]);
            setBulkConfirm({ open: false, action: null });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "İşlem başarısız.");
        } finally {
            setBusy(false);
        }
    };

    const clearFilters = () => {
        setSearch("");
        setDepartment("");
        setRole("");
        setStatus("");
        setPage(1);
    };

    const exportColumns = [
        { header: "Ad Soyad", key: "full_name", width: 24 },
        { header: "E-Posta", key: "email", width: 28 },
        { header: "Telefon", key: "phone", width: 18 },
        { header: "Departman", key: "department", width: 20 },
        { header: "Pozisyon", key: "title", width: 22 },
        { header: "Rol", key: "role", width: 16 },
        { header: "Durum", key: "status", width: 12 },
        { header: "Son Giriş", key: "last_login_at", width: 20 },
    ];

    const exportRows = () => rows.map((r) => ({
        ...r,
        last_login_at: r.last_login_at ? new Date(r.last_login_at).toLocaleString("tr-TR") : "—",
        status: tr.status[r.status] || r.status,
    }));

    if (!canView) return <Forbidden />;

    const columns = [
        {
            key: "full_name", header: tr.personnel.columns.fullName, sortable: true,
            render: (r) => (
                <Link to={`/personnel/${r.id}`} className="block">
                    <UserCell name={r.full_name} subtitle={r.email} avatarUrl={r.avatar_url} />
                </Link>
            )
        },
        { key: "department", header: tr.personnel.columns.department, sortable: true,
          render: (r) => <span className="text-[13px]">{r.department || "—"}</span> },
        { key: "title", header: tr.personnel.columns.title, sortable: true,
          render: (r) => <span className="text-[13px]">{r.title || "—"}</span> },
        { key: "phone", header: tr.personnel.columns.phone,
          render: (r) => <span className="text-[13px] tabular-nums">{r.phone || "—"}</span> },
        { key: "status", header: tr.personnel.columns.status, sortable: true,
          render: (r) => <StatusBadge status={r.status || (r.is_active ? "active" : "inactive")} /> },
        { key: "last_login_at", header: tr.personnel.columns.lastLogin, sortable: true,
          render: (r) => <span className="text-[12.5px] text-muted-foreground">{r.last_login_at ? formatDate(r.last_login_at) : tr.common.never}</span> },
        {
            key: "actions", header: tr.personnel.columns.actions, align: "right",
            render: (r) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted" aria-label="İşlemler" data-testid={`row-actions-${r.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel>{tr.common.actions}</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => navigate(`/personnel/${r.id}`)}>
                            <Eye className="mr-2 h-4 w-4" /> {tr.common.details}
                        </DropdownMenuItem>
                        {canEdit && (
                            <DropdownMenuItem onClick={() => navigate(`/personnel/${r.id}?tab=edit`)}>
                                <Filter className="mr-2 h-4 w-4" /> {tr.common.edit}
                            </DropdownMenuItem>
                        )}
                        {canDelete && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                                    onClick={async () => {
                                        if (!window.confirm(tr.personnel.detail.confirmDeleteBody)) return;
                                        try {
                                            await api.delete(`/employees/${r.id}`);
                                            toast.success("Personel silindi.");
                                            load();
                                        } catch (e) {
                                            toast.error(e?.response?.data?.detail || "Silinemedi.");
                                        }
                                    }}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" /> {tr.common.delete}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    ];

    const filtersActive = search || department || role || status;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight leading-tight">{tr.personnel.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.personnel.subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Can permission="employees.export">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted" data-testid="btn-export">
                                    <Download className="h-4 w-4" /> Dışa Aktar
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => exportToExcel({ filename: "personeller", sheetName: "Personeller", columns: exportColumns, rows: exportRows() })}>
                                    <FileSpreadsheet className="mr-2 h-4 w-4" /> {tr.common.exportExcel}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportToPdf({ filename: "personeller", title: "Personel Listesi", columns: exportColumns, rows: exportRows() })}>
                                    <FileText className="mr-2 h-4 w-4" /> {tr.common.exportPdf}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </Can>
                    {canCreate && (
                        <Link to="/personnel/new" className="btn-brand h-11" data-testid="btn-new-personnel">
                            <Plus className="h-4 w-4" /> {tr.personnel.newPersonnel}
                        </Link>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatMini Icon={Users} label={tr.personnel.totalPersonnel} value={stats.total} accent="bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" />
                <StatMini Icon={UserCheck} label={tr.personnel.activePersonnel} value={stats.active} accent="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" />
                <StatMini Icon={Clock} label={tr.personnel.onLeave} value={stats.onLeave} accent="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" />
                <StatMini Icon={TrendingUp} label={tr.personnel.newThisMonth} value="3" accent="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]" />
            </div>

            {/* Filters */}
            <div className="card-elevated p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative md:col-span-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Ad, e-posta veya telefon ile ara…"
                            className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                            data-testid="filter-search"
                        />
                    </div>
                    <select value={department} onChange={(e) => { setDepartment(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-department">
                        <option value="">{tr.personnel.filters.allDepartments}</option>
                        {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                    <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-role">
                        <option value="">{tr.personnel.filters.allRoles}</option>
                        {roles.map((r) => <option key={r.name} value={r.name}>{r.display_name}</option>)}
                    </select>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-status">
                        <option value="">{tr.personnel.filters.allStatuses}</option>
                        <option value="active">{tr.status.active}</option>
                        <option value="inactive">{tr.status.inactive}</option>
                        <option value="on_leave">{tr.status.on_leave}</option>
                        <option value="terminated">{tr.status.terminated}</option>
                    </select>
                </div>
                {filtersActive && (
                    <div className="mt-3 flex items-center gap-2 text-[12px] text-muted-foreground">
                        <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 hover:bg-muted" data-testid="clear-filters">
                            <X className="h-3 w-3" /> {tr.common.clearFilters}
                        </button>
                    </div>
                )}
            </div>

            {/* Bulk actions */}
            {selectedIds.length > 0 && canManage && (
                <div className="flex items-center justify-between rounded-2xl border border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand))]/5 px-4 py-3">
                    <div className="text-[13px]">
                        <span className="font-semibold text-foreground">{selectedIds.length}</span> {tr.common.selected}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setBulkConfirm({ open: true, action: "activate" })} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-[12.5px] font-semibold hover:bg-muted">
                            <ToggleRight className="h-4 w-4" /> {tr.common.activate}
                        </button>
                        <button onClick={() => setBulkConfirm({ open: true, action: "deactivate" })} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-[12.5px] font-semibold hover:bg-muted">
                            <ToggleLeft className="h-4 w-4" /> {tr.common.deactivate}
                        </button>
                        {canDelete && (
                            <button onClick={() => setBulkConfirm({ open: true, action: "delete" })} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 text-[hsl(var(--destructive))] px-3 py-1.5 text-[12.5px] font-semibold hover:bg-[hsl(var(--destructive))]/10">
                                <Trash2 className="h-4 w-4" /> {tr.common.deleteSelected}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Table */}
            <DataTable
                columns={columns}
                rows={rows}
                loading={loading}
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                sort={sort}
                order={order}
                onSortChange={(k, o) => { setSort(k); setOrder(o); }}
                selectable={canManage}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
            />

            <ConfirmDialog
                open={bulkConfirm.open}
                onOpenChange={(o) => setBulkConfirm({ ...bulkConfirm, open: o })}
                title={bulkConfirm.action === "delete" ? tr.common.confirmDelete : tr.common.confirm}
                description={
                    bulkConfirm.action === "activate" ? tr.personnel.bulk.activateConfirm :
                    bulkConfirm.action === "deactivate" ? tr.personnel.bulk.deactivateConfirm :
                    tr.personnel.bulk.deleteConfirm
                }
                confirmText={bulkConfirm.action === "delete" ? tr.common.yesDelete : tr.common.confirm}
                variant={bulkConfirm.action === "delete" ? "danger" : "default"}
                loading={busy}
                onConfirm={doBulk}
            />
        </div>
    );
}
